import { useTranslation } from 'react-i18next';
import { useShare } from '../../hooks/useShare';
import { canUseNativeShare, type ShareChannel, type SharePayload } from '../../utils/share';

type ShareVariant = 'header' | 'sidebar' | 'footer';

interface ChannelItem {
  channel: ShareChannel;
  icon: string;
  labelKey?: string;
  fixedLabel?: string;
}

const CHANNEL_ITEMS: Record<ShareChannel, ChannelItem> = {
  twitter: { channel: 'twitter', icon: 'ri-twitter-x-line', fixedLabel: 'X' },
  linkedin: { channel: 'linkedin', icon: 'ri-linkedin-box-line', fixedLabel: 'LinkedIn' },
  telegram: { channel: 'telegram', icon: 'ri-telegram-line', fixedLabel: 'Telegram' },
  copy: { channel: 'copy', icon: 'ri-link-line', labelKey: 'newsDetail.copyLink' },
  native: { channel: 'native', icon: 'ri-share-forward-line', labelKey: 'share.native' },
};

/** Footer 第四项：微信 Web 无法直跳，复制当前页链接 */
const FOOTER_WECHAT: ChannelItem = {
  channel: 'copy',
  icon: 'ri-wechat-line',
  labelKey: 'contact.social.wechat',
};

interface ShareButtonsProps {
  /** footer 变体固定为 X / LinkedIn / Telegram / 微信(复制链接) */
  channels?: ShareChannel[];
  payload?: SharePayload;
  variant?: ShareVariant;
  className?: string;
}

export default function ShareButtons({
  channels,
  payload,
  variant = 'header',
  className = '',
}: ShareButtonsProps) {
  const { t } = useTranslation();
  const { handleShare, copied } = useShare(payload);

  const resolvedChannels = channels ?? [];

  const items: ChannelItem[] =
    variant === 'footer'
      ? [
          CHANNEL_ITEMS.twitter,
          { ...CHANNEL_ITEMS.linkedin, icon: 'ri-linkedin-line' },
          CHANNEL_ITEMS.telegram,
          FOOTER_WECHAT,
        ]
      : resolvedChannels.map((ch) => CHANNEL_ITEMS[ch]);

  const getLabel = (item: ChannelItem) =>
    item.labelKey ? t(item.labelKey) : item.fixedLabel ?? '';

  const isCopiedChannel = (item: ChannelItem) =>
    copied && (item.channel === 'copy' || item.icon === 'ri-link-line' || item.icon === 'ri-wechat-line');

  if (variant === 'footer') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        {items.map((item) => {
          const label = getLabel(item);
          return (
            <button
              key={item.icon}
              type="button"
              title={isCopiedChannel(item) ? t('share.linkCopied') : label}
              aria-label={label}
              onClick={() => void handleShare(item.channel)}
              className="w-9 h-9 flex items-center justify-center bg-white border border-primary-200 rounded-lg text-gray-400 hover:text-primary hover:border-primary/40 transition-all duration-200 cursor-pointer"
            >
              <i
                className={`${isCopiedChannel(item) ? 'ri-check-line text-primary' : item.icon} text-sm`}
              />
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === 'sidebar') {
    const showNative = canUseNativeShare();
    const sidebarChannels = showNative
      ? [...resolvedChannels, 'native' as ShareChannel]
      : resolvedChannels;
    const sidebarItems = sidebarChannels.map((ch) => CHANNEL_ITEMS[ch]);

    return (
      <div className={`flex gap-2 ${className}`}>
        {sidebarItems.map((item) => {
          const label = getLabel(item);
          const copiedActive = copied && (item.channel === 'copy' || item.channel === 'native');
          return (
            <button
              key={item.channel}
              type="button"
              title={copiedActive ? t('share.linkCopied') : label}
              aria-label={label}
              onClick={() => void handleShare(item.channel)}
              className="flex-1 py-2.5 bg-white border border-primary/10 rounded-lg text-gray-400 hover:text-primary hover:border-primary/25 transition-all duration-200 cursor-pointer flex items-center justify-center"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`${copiedActive ? 'ri-check-line text-primary' : item.icon} text-sm`}
                />
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  // header — compact icon row
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {resolvedChannels.map((ch) => {
        const item = CHANNEL_ITEMS[ch];
        const label = getLabel(item);
        const copiedActive = copied && item.channel === 'copy';
        return (
          <button
            key={ch}
            type="button"
            title={copiedActive ? t('share.linkCopied') : label}
            aria-label={label}
            onClick={() => void handleShare(ch)}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-primary/10 text-gray-400 hover:text-primary hover:border-primary/25 transition-all duration-200 cursor-pointer"
          >
            <i className={`${copiedActive ? 'ri-check-line text-primary' : item.icon} text-sm`} />
          </button>
        );
      })}
    </div>
  );
}
